import BenchlingConfig from 'BenchlingConfig';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

type BenchlingConfigKey = keyof typeof BenchlingConfig;

export type ChatInputTipType = 'standard' | 'promotional';

export interface ChatInputTip {
  id: string;
  icon: BenchlingIconName;
  title: string;
  text: string;
  tipType?: ChatInputTipType;
  /**
   * Agent types referenced by the tip. The tip will only be shown if ALL listed agent
   * types are enabled for the tenant/user (see `isAgentTypeEnabled`).
   */
  requiredAgentTypes?: ReadonlyArray<AgentType>;
  /**
   * BenchlingConfig keys that must all be truthy for the tip to be shown.
   */
  requiresBenchlingConfigs?: ReadonlyArray<BenchlingConfigKey>;
  /** Label for the action button (promotional tips only). */
  buttonLabel?: string;
  /** Static image paths for preview icons shown alongside the text (promotional tips only). */
  previewIconPaths?: ReadonlyArray<string>;
  /**
   * Frequency weight for tip selection. Higher values cause the tip to appear more
   * often relative to tips with weight 1 (the default).
   */
  weight?: number;
}

function getRequiredAgentTypes(agentType: AgentType): ReadonlyArray<AgentType> {
  return BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT ? [AgentType.ASSISTANT] : [agentType];
}

export default function getChatInputTips(): ReadonlyArray<ChatInputTip> {
  const usesAssistantAgent = BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT;
  return [
    {
      id: 'promo-mcp-connectors',
      icon: 'plug',
      title: 'Connect to your favorite tools',
      text: 'Connect to your favorite tools',
      tipType: 'promotional',
      requiresBenchlingConfigs: ['ENABLE_EXTENSION_DIRECTORY'],
      buttonLabel: 'Browse',
      previewIconPaths: [
        '/static/img/extensions/snowflake.png',
        '/static/img/extensions/sharepoint.svg',
        '/static/img/extensions/notion.png',
      ],
      weight: 3,
    },
    {
      id: '1',
      icon: 'lightbulb',
      title: 'Skills',
      text: 'Level up your agent with Skills — reusable prompts that shape how agents respond. Click the gear icon in the top right to view or add them.',
    },
    {
      id: '2',
      icon: 'settings',
      title: 'Customize behavior',
      text: "Make Benchling AI work the way you do. Click the gear icon in the top right to customize your agent's tone, persona, and more.",
    },
    {
      id: '3',
      icon: 'line-chart',
      title: 'Dynamic plots',
      text: usesAssistantAgent
        ? 'Start a chat and turn your data into interactive charts and visualizations.'
        : 'Use Deep Research to turn your data into interactive charts and visualizations.',
      requiredAgentTypes: getRequiredAgentTypes(AgentType.DEEP_RESEARCH),
    },
    {
      id: '4',
      icon: 'protocol-image',
      title: 'Image generation',
      text: usesAssistantAgent
        ? 'Use natural language to generate illustrations or figures for your experiments.'
        : 'Use Deep Research to generate illustrations or figures for your experiments.',
      requiredAgentTypes: getRequiredAgentTypes(AgentType.DEEP_RESEARCH),
      requiresBenchlingConfigs: ['LLM_ENABLE_GOOGLE_CLOUD'],
    },
    {
      id: '5',
      icon: 'sparkles',
      title: 'Deep Research',
      text: usesAssistantAgent
        ? 'Ask complex questions and dig deeper into your data.'
        : 'Ask complex questions and dig deeper into your data by selecting Deep Research.',
      requiredAgentTypes: getRequiredAgentTypes(AgentType.DEEP_RESEARCH),
    },
    {
      id: '6',
      icon: 'file-arrow-up',
      title: 'File upload',
      text: 'Drag and drop files directly into the chat. Upload templates, protocols, or files with context to get more relevant answers.',
    },
    {
      id: '7',
      icon: 'warehouse-table',
      title: 'Data capture',
      text: usesAssistantAgent
        ? "Use agents to import data directly into your draft's structured tables."
        : "Use Compose to import data directly into your draft's structured tables.",
      requiredAgentTypes: getRequiredAgentTypes(AgentType.DATA_CAPTURE),
    },
    {
      id: '8',
      icon: 'lightbulb',
      title: 'Help Center',
      text: usesAssistantAgent
        ? 'Ask and learn how a feature works on Benchling without leaving your workspace.'
        : 'Use Ask to learn how a feature works on Benchling without leaving your workspace.',
      requiredAgentTypes: getRequiredAgentTypes(AgentType.ASK),
    },
    {
      id: '9',
      icon: 'save',
      title: 'Saving responses',
      text: usesAssistantAgent
        ? 'Click "Save as" in agent responses to add the content to a new entry, new template, new Word document, or an existing entry.'
        : 'Click "Save as" in the Deep Research response to add the content to a new entry, new template, new Word document, or an existing entry.',
      requiredAgentTypes: getRequiredAgentTypes(AgentType.DEEP_RESEARCH),
    },
    {
      id: '10',
      icon: 'edit',
      title: 'Kickstart drafting',
      text: usesAssistantAgent
        ? 'Iterate on your work by pasting a link to a previous entry or template in your chat.'
        : 'Iterate on your work by pasting a link to a previous entry or template in your Compose chat.',
      requiredAgentTypes: getRequiredAgentTypes(AgentType.DATA_CAPTURE),
    },
  ];
}
